import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MDBContainer } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import { useAuth } from "../context/AuthContext";
import { formatDisplayPrice } from "../utils/displayPrice";
import { normalizeRole } from "../utils/rolePermissions";
import { getCurrencySymbol, formatPriceWithCurrency } from "../utils/currency";
import api from "../services/api";
import { fetchOrders } from "../services/ordersApi";
import "./ViewProductsPage.css";

const marketplaces = [
  { id: "ebay", name: "eBay", color: "primary" },
  { id: "shopify", name: "Shopify", color: "success" },
  { id: "poshmark", name: "Poshmark", color: "secondary" },
  { id: "vinted", name: "Vinted", color: "info" },
];

// Simple retry image component – tries to reload original src up to 3 times
const RetryImage = ({ src, alt, className }) => {
  const imgRef = useRef(null);
  const retryCount = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds

  const handleError = () => {
    if (retryCount.current < MAX_RETRIES) {
      retryCount.current += 1;
      setTimeout(() => {
        if (imgRef.current) {
          // Force reload same URL with timestamp to bypass cache
          imgRef.current.src = src + (src.includes('?') ? '&' : '?') + 't=' + Date.now();
        }
      }, RETRY_DELAY * retryCount.current);
    } else {
      // All retries failed, show placeholder
      if (imgRef.current) {
        imgRef.current.onerror = null;
        imgRef.current.src = "https://via.placeholder.com/120";
      }
    }
  };

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={handleError}
    />
  );
};

const STATUS_TABS = [
  { key: "", label: "All Products" },
  { key: "pending_approval", label: "Pending Approval" },
  { key: "listed", label: "Listed" },
  { key: "sold", label: "Sold" },
];

const STATUS_TAB_ROLES = ["owner", "admin", "seller manager", "editor"];

const getUserDisplayName = (u) => {
  if (!u) return "";
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return u.name || fullName || u.email || "";
};

const resolveUploadedByName = (product, currentUser) => {
  const fromApi =
    product.uploadedBy?.name ||
    product.uploaded_by_name ||
    getUserDisplayName(product.uploadedBy);

  if (fromApi) return fromApi;

  const uploadedById = product.uploadedBy?.id ?? product.uploaded_by;
  const currentUserId =
    currentUser?.id || currentUser?.user_id || currentUser?.userId;

  if (
    uploadedById != null &&
    currentUserId != null &&
    String(uploadedById) === String(currentUserId)
  ) {
    return getUserDisplayName(currentUser) || "You";
  }

  return "";
};

const ViewProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { displayPricePercentage, user, companyId } = useAuth();
  const userId = user?.id || user?.user_id || user?.userId;
  const userRoleNormalized = normalizeRole(user?.role);
  const isOwnerOrAdmin = ["owner", "admin"].includes(userRoleNormalized);
  const canSeeStatusTabs = STATUS_TAB_ROLES.includes(userRoleNormalized);
const isEditor = userRoleNormalized === "editor";
  const statusFilter = searchParams.get("status") || "";

  const { state, setState, clearState } = usePageState("viewProducts");
 // eslint-disable-next-line 
  const { setState: setAddProductState } = usePageState("addProduct");
  const BASE_URL =
    "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

  const {
    currentPage = 1,
    itemsPerPage = 5,
    searchQuery = "",
    categoryFilter = "",
    marketplaceFilter = "",
    sellerFilter = "",
    selectedProductIds = [],
    expandedProduct = null,
    cachedProducts = null,
    lastFetched = null,
    cachedStatus = null,
  } = state;

  const [products, setProducts] = useState(cachedProducts || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyCurrency, setCompanyCurrency] = useState("USD");
const [bulkUploading, setBulkUploading] = useState(false);
const [orderStatusMap, setOrderStatusMap] = useState({});
  // ---- Company currency ----
  useEffect(() => {
    if (!companyId) return;
    const fetchCurrency = async () => {
      try {
        const res = await api.get(`/api/general-settings/${companyId}`);
        const currency = res.data?.currency;
        if (currency) setCompanyCurrency(currency);
      } catch (err) {
        console.error("Failed to fetch company currency:", err);
      }
    };
    fetchCurrency();
  }, [companyId]);

    // ---- Fetch orders to show pipeline status on products ----
  useEffect(() => {
    if (!companyId || !userId) return;
    const fetchOrderStatuses = async () => {
      try {
        const result = await fetchOrders({
          companyId,
          userId,
          userRole: normalizeRole(user?.role),
        });
        const orders = result.data || [];
        // Build a map: product_id → { seller_status, rider_status, qc_status, outbound_status, order_id }
        const map = {};
        orders.forEach((order) => {
          (order.items || []).forEach((item) => {
            const pid = item.product_id;
            if (pid) {
              map[pid] = {
                seller_status: order.seller_status,
                rider_status: order.rider_status,
                qc_status: order.qc_status,
                outbound_status: order.outbound_status,
                order_id: order.id,
              };
            }
          });
        });
        setOrderStatusMap(map);
      } catch (err) {
        console.error("Failed to fetch order statuses:", err);
      }
    };
    fetchOrderStatuses();
  }, [companyId, userId, user?.role]);

  // ---- Price formatter ----
  const formatPriceInner = useCallback(
    (amount) =>
      isOwnerOrAdmin
        ? formatPriceWithCurrency(amount, companyCurrency)
        : formatDisplayPrice(amount, displayPricePercentage, {
            prefix: getCurrencySymbol(companyCurrency),
          }),
    [isOwnerOrAdmin, companyCurrency, displayPricePercentage],
  );

  // ---- Fetch products ----
  const fetchProducts = useCallback(
    async (force = false) => {
      const now = Date.now();
      const CACHE_TTL = 5 * 60 * 1000;

      const cacheIsValid =
        !force &&
        cachedProducts &&
        lastFetched &&
        now - lastFetched < CACHE_TTL &&
        cachedStatus === statusFilter;

      if (cacheIsValid) {
        setProducts(cachedProducts);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: productsRes } = await api.get("/api/products", {
          params: {
            company_id: companyId,
            user_id: userId,
            ...(statusFilter ? { status: statusFilter } : {}),
          },
        });

        const productsData = Array.isArray(productsRes?.data)
          ? productsRes.data
          : productsRes?.data?.products || productsRes?.products || [];

        const productIds = productsData.map((p) => p.id);
        let ebayMap = {};
        let shopifyMap = {};
        let vintedMap = {};

        if (productIds.length > 0) {
          try {
            // ✅ Using Promise.allSettled so one failure doesn't break all
            const results = await Promise.allSettled([
              api.post("/api/ebay-product-batch", { product_ids: productIds }),
              api.post("/api/shopify-product-batch", { product_ids: productIds }),
              // ✅ NOW USING LOCAL PROXY ROUTE (same domain, no CORS)
              api.post("/api/vinted-product-batch", { product_ids: productIds }),
            ]);

            // eBay
            if (results[0].status === "fulfilled") {
              ebayMap = results[0].value.data?.data || {};
            } else {
              console.error("eBay batch failed:", results[0].reason);
            }

            // Shopify
            if (results[1].status === "fulfilled") {
              shopifyMap = results[1].value.data?.data || {};
            } else {
              console.error("Shopify batch failed:", results[1].reason);
            }

            // Vinted
            if (results[2].status === "fulfilled") {
              vintedMap = results[2].value.data?.data || {};
            } else {
              console.error("Vinted batch failed:", results[2].reason);
            }
          } catch (err) {
            console.error("Batch fetch error:", err);
          }
        }

        const productsWithMarketplaces = productsData.map((product) => {
          const ebayData =
            ebayMap[product.id] ?? ebayMap[String(product.id)] ?? null;
          const shopifyData =
            shopifyMap[product.id] ?? shopifyMap[String(product.id)] ?? null;
          const vintedData =
            vintedMap[product.id] ?? vintedMap[String(product.id)] ?? null;

const hasEbay = ebayData && ebayData.ebay_item_id && ebayData.ebay_item_id !== "Not listed";
          const hasShopify = shopifyData && Object.keys(shopifyData).length > 0;
          const hasVinted = vintedData && Object.keys(vintedData).length > 0;

          const marketplaceDetails = {};

          const categoryName =
            product.category?.name || product.category || "Other";
          const uploadedByName = resolveUploadedByName(product, user);
          const conditionValue =
            product.condition ||
            product.condition_name ||
            product.item_condition ||
            ebayData?.condition ||
            null;

          marketplaceDetails.local = {
            Title: product.title || "",
            Description: product.description || "",
            SKU: product.sku || "",
            Price: formatPriceInner(product.price || 0),
            Quantity: product.quantity || 0,
            Category: categoryName,
            Condition: conditionValue || "Not specified",
            ...(uploadedByName ? { "Uploaded By": uploadedByName } : {}),
          };

          if (hasVinted) {
            marketplaceDetails.vinted = {
              "Vinted ID": vintedData.id || "Not synced",
              "Internal Product ID": vintedData.internal_product_id || "",
              Title: vintedData.title || "",
              Description: vintedData.description || "",
              Price: formatPriceInner(vintedData.price || 0),
              Category: vintedData.category || "",
              Brand: vintedData.brand || "",
              Size: vintedData.size || "",
              Condition: vintedData.condition || "",
              "Package Size": vintedData.package_size || "",
              Color: vintedData.color || "",
              "Vinted Product ID": vintedData.product_id || "",
              Status: vintedData.status || "draft",
            };
          }

          if (hasEbay) {
            const ebayFields = {};

            if (ebayData.aspects) {
              let aspectsObj = ebayData.aspects;
              if (typeof aspectsObj === "string") {
                try {
                  aspectsObj = JSON.parse(aspectsObj);
                } catch {
                  aspectsObj = {};
                }
              }
              Object.entries(aspectsObj || {}).forEach(([key, value]) => {
                const val = Array.isArray(value) ? value.join(", ") : value;
                if (val && val !== "N/A") ebayFields[key] = val;
              });
            }

            ebayFields["eBay Item ID"] = ebayData.ebay_item_id || "Not listed";
            ebayFields.SKU = ebayData.sku || product.sku || "";
            ebayFields.Price = formatPriceInner(
              ebayData.price || product.price || 0,
            );
            ebayFields.Quantity = ebayData.quantity || 0;
            ebayFields.Condition =
              ebayData.condition || product.condition || "";
            ebayFields["Listing Type"] =
              ebayData.listing_type || "Not specified";
            ebayFields.Duration = ebayData.duration || "Not specified";

            marketplaceDetails.ebay = ebayFields;
          }

          if (hasShopify) {
            const shopifyFields = {};

            shopifyFields["Shopify Product ID"] =
              shopifyData.shopify_product_id || "Not synced";
            shopifyFields["Variant ID"] =
              shopifyData.shopify_variant_id || "Not synced";
            shopifyFields["Inventory Item ID"] =
              shopifyData.inventory_item_id || "Not synced";
            shopifyFields.SKU = shopifyData.sku || product.sku || "";
            shopifyFields.Price = formatPriceInner(
              shopifyData.price || product.price || 0,
            );
            shopifyFields.Quantity = shopifyData.quantity || 0;
            shopifyFields.Vendor = shopifyData.vendor || "";
            shopifyFields["Product Type"] =
              shopifyData.product_type || product.category || "";
            shopifyFields.Status = shopifyData.status || "draft";

            marketplaceDetails.shopify = shopifyFields;
          }

          return {
            id: product.id,
            title: product.title,
            description: product.description,
            price: product.price,
            category: categoryName,
            condition: conditionValue,
            quantity: product.quantity,
            sku: product.sku,
            status: product.status || null,
            uploadedBy: {
              id: product.uploadedBy?.id ?? product.uploaded_by ?? null,
              name: uploadedByName,
            },
            images: product.images?.length
              ? product.images
              : product.primaryImage
                ? [product.primaryImage]
                : ["https://via.placeholder.com/120"],
            selectedMarketplaces: [
              "local",
              ...(hasEbay ? ["ebay"] : []),
              ...(hasShopify ? ["shopify"] : []),
              ...(hasVinted ? ["vinted"] : []),
            ],
            marketplaceDetails,
          };
        });

        setProducts(productsWithMarketplaces);
        setState({
          cachedProducts: productsWithMarketplaces,
          lastFetched: Date.now(),
          cachedStatus: statusFilter,
        });
      } catch (err) {
        console.error("API ERROR:", err);
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load products. Please try again.",
        );
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      companyId,
      userId,
      statusFilter,
      cachedProducts,
      lastFetched,
      cachedStatus,
      setState,
      formatPriceInner,
    ],
  );

  useEffect(() => {
    if (userId && companyId) {
      fetchProducts();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, companyId, statusFilter]);

  const handleStatusTabChange = (statusKey) => {
    if (statusKey) {
      setSearchParams({ status: statusKey });
    } else {
      setSearchParams({});
    }
    setState({ currentPage: 1, selectedProductIds: [] });
  };

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))],
    [products],
  );

  const sellers = useMemo(
    () => [...new Set(products.map((p) => p.uploadedBy?.name).filter(Boolean))],
    [products],
  );

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return products.filter((product) => {
      const matchesSearch =
        q === "" ||
        product.title?.toLowerCase().includes(q) ||
        product.sku?.toLowerCase().includes(q) ||
        product.description?.toLowerCase().includes(q);

      const matchesCategory =
        categoryFilter === "" || product.category === categoryFilter;
      const matchesMarketplace =
        marketplaceFilter === "" ||
        product.selectedMarketplaces.includes(marketplaceFilter);
      const matchesSeller =
        sellerFilter === "" || product.uploadedBy?.name === sellerFilter;

      return (
        matchesSearch && matchesCategory && matchesMarketplace && matchesSeller
      );
    });
  }, [products, searchQuery, categoryFilter, marketplaceFilter, sellerFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / itemsPerPage),
  );
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setState({ currentPage: page });
  };

  const handleSelectProduct = (productId) => {
    const isSelected = selectedProductIds.includes(productId);
    const newSelection = isSelected
      ? selectedProductIds.filter((id) => id !== productId)
      : [...selectedProductIds, productId];
    setState({ selectedProductIds: newSelection });
  };

  // eslint-disable-next-line
  const handleAddProductCopy = async (product) => {
    try {
      const sellerUserId = product.uploadedBy?.id;
      const existingSku = product.sku;
      let finalSku = existingSku;

      // SKU check
      try {
        const res = await fetch(
          `${BASE_URL}/api/v1/checkAndGetSku?user_id=${encodeURIComponent(
            sellerUserId
          )}&existing_sku=${encodeURIComponent(existingSku)}`
        );
        const data = await res.json();
        if (data.success) {
          finalSku = data.sku;
        }
      } catch (err) {
        console.error("SKU check failed:", err);
      }

      // Full product detail fetch
      let fullProductData = null;
      try {
        const token = localStorage.getItem("token");
        const fullRes = await fetch(`${BASE_URL}/api/product-full/${product.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fullJson = await fullRes.json();
        if (fullJson.success && fullJson.data) {
          fullProductData = fullJson.data;
        }
      } catch (err) {
        console.error("Full product fetch failed:", err);
      }

      const { product: fullProduct, ebay } = fullProductData || {};

      // raw_response se saved formData
      let savedFormData = {};
      if (fullProduct?.raw_response) {
        try {
          let raw = fullProduct.raw_response;
          if (typeof raw === "string") raw = JSON.parse(raw);
          if (raw.formData) savedFormData = raw.formData;
        } catch (err) {
          console.warn("Failed to parse raw_response", err);
        }
      }

      // eBay aspects
      const ebayFields = {};
      if (ebay && ebay.aspects) {
        let aspects = ebay.aspects;
        if (typeof aspects === "string") aspects = JSON.parse(aspects);
        Object.entries(aspects).forEach(([key, value]) => {
          let val = Array.isArray(value) ? value.join(", ") : value;
          if (val && val !== "N/A" && val.trim() !== "") {
            let mappedKey = key;
            if (key === "Colour") mappedKey = "Color";
            ebayFields[mappedKey] = val;
          }
        });
      }

      const finalBrand =
        ebayFields.Brand || savedFormData.brand || fullProduct?.brand || "";
      const finalColor =
        ebayFields.Color || savedFormData.color || fullProduct?.color || "";
      const finalMaterial =
        ebayFields.Material || savedFormData.material || fullProduct?.material || "";
      const finalTags = savedFormData.tags || fullProduct?.tags || "";
      const finalVariants = savedFormData.variants?.length
        ? savedFormData.variants
        : [{ id: 1, size: ebayFields.Size || "", color: finalColor, sku: "" }];
      const finalCategoryAspects = savedFormData.categoryAspects || [];
      const finalAllCategoryAspects = savedFormData.allCategoryAspects || [];
      const finalCategoryId = savedFormData.categoryId || "";
      const finalConditionScore = savedFormData.condition_score || "";
      const finalConditionSummary = savedFormData.condition_summary || "";
      const finalAuthenticityScore = savedFormData.authenticity_score || "";
      const finalAuthenticitySummary = savedFormData.authenticity_summary || "";
      const finalVintedSize = savedFormData.vintedSize || ebayFields.Size || "";

      // Prepare formData object
      const formData = {
        currentStep: 2,
        formData: {
          uploadedImages: fullProduct?.images?.length
            ? fullProduct.images
            : product.images?.filter((img) => !img.includes("placeholder")) || [],
          blurStatuses: [],
          imageProcessing: false,
          backgroundRemoval: false,
          blurDetection: false,
          upscaleImage: false,
          title: fullProduct?.title || product.title || "",
          description: fullProduct?.description || product.description || "",
          sku: finalSku,
          _existing_product_id: product.id,
          price: String(fullProduct?.price ?? product.price ?? ""),
          quantity: String(fullProduct?.quantity ?? product.quantity ?? "1"),
          category: savedFormData.category || product.category || "",
          condition: fullProduct?.condition || product.condition || "",
          brand: finalBrand,
          color: finalColor,
          material: finalMaterial,
          tags: finalTags,
          metafields: savedFormData.metafields || {},
          categoryAspects: finalCategoryAspects,
          allCategoryAspects: finalAllCategoryAspects,
          categoryId: finalCategoryId,
          condition_score: finalConditionScore,
          condition_summary: finalConditionSummary,
          authenticity_score: finalAuthenticityScore,
          authenticity_summary: finalAuthenticitySummary,
          variants: finalVariants,
          vintedSize: finalVintedSize,
          selectedMarketplaces:
            product.selectedMarketplaces?.filter((m) => m !== "local") || [],
          poshmarkCategory: savedFormData.poshmarkCategory || "Women",
          poshmarkSubCategory: savedFormData.poshmarkSubCategory || "",
          vintedCategory: savedFormData.vintedCategory || "",
          vintedCategoryId: savedFormData.vintedCategoryId || "",
          vintedCategoryPath: savedFormData.vintedCategoryPath || [],
          vintedPackageSize: savedFormData.vintedPackageSize || "medium",
          seller_id: sellerUserId,
        },
      };

      // Unique ID for sessionStorage
      const copyId = `copy_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 8)}`;
      sessionStorage.setItem(copyId, JSON.stringify(formData));

      // Open new tab
      window.open(`/products/add?copyId=${copyId}`, "_blank");
    } catch (error) {
      console.error("Error preparing product copy:", error);
      alert("Kuch galat ho gaya, dubara try karo.");
    }
  };

  const handleSelectAll = () => {
    const allIds = paginatedProducts.map((p) => p.id);
    const allSelected =
      allIds.length > 0 &&
      allIds.every((id) => selectedProductIds.includes(id));

    if (allSelected) {
      setState({
        selectedProductIds: selectedProductIds.filter(
          (id) => !allIds.includes(id)
        ),
      });
    } else {
      setState({
        selectedProductIds: [...new Set([...selectedProductIds, ...allIds])],
      });
    }
  };

  const handleToggleExpand = (productId) => {
    setState({
      expandedProduct: expandedProduct === productId ? null : productId,
    });
  };

  const handleClearFilters = () => {
    clearState();
  };

const handleBulkUploadVinted = async () => {
    if (selectedProductIds.length === 0) return;
    if (!companyId) return;

    console.log('🚀 Bulk upload starting...');
    console.log('   Actual User ID:', userId);
    console.log('   selectedProductIds:', selectedProductIds);

    setBulkUploading(true);

    try {
        // ✅ STEP 0 – SYNC USERID TO EXTENSION BEFORE BULK UPLOAD
        try {
            await fetch('https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: userId })   // actual user ID, not companyId
            });
            console.log('✅ UserId synced with extension');
            // Wait 2 seconds for extension to re‑register with new userId
            await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (syncErr) {
            console.warn('⚠️ UserId sync failed, proceeding anyway:', syncErr);
        }

        // ✅ STEP 1 – SEND BULK UPLOAD REQUEST
        const payload = {
            userId: userId,             // actual logged‑in user ID (58)
            productIds: selectedProductIds,
        };
        console.log('📤 Payload:', payload);

        const res = await fetch(
            'https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/bulk-upload-vinted',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }
        );

        const data = await res.json();
        console.log('📦 Response status:', res.status);
        console.log('📦 Response body:', data);

        if (data.success) {
            alert(`✅ Bulk upload started! ${data.processed} products queued.`);
            setState({ selectedProductIds: [] });
        } else {
            console.error('❌ Server returned error:', data.error);
            alert('❌ Bulk upload failed: ' + data.error);
        }
    } catch (err) {
        console.error('❌ Network error:', err);
        alert('❌ Network error: ' + err.message);
    } finally {
        setBulkUploading(false);
    }
};

  return (
    <MDBContainer fluid className="view-products-container">
      <h2 className="mb-4">View Products</h2>

      {canSeeStatusTabs && (
        <div
          className="status-tabs"
          role="tablist"
          aria-label="Filter products by status"
        >
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key || "all"}
              type="button"
              role="tab"
              aria-selected={statusFilter === tab.key}
              className={`status-tab-btn ${
                statusFilter === tab.key ? "active" : ""
              }`}
              onClick={() => handleStatusTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-item">
            <label htmlFor="search-products">Search</label>
            <input
              id="search-products"
              type="text"
              className="filter-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) =>
                setState({ searchQuery: e.target.value, currentPage: 1 })
              }
            />
          </div>

          <div className="filter-item">
            <label htmlFor="category-filter">Category</label>
            <select
              id="category-filter"
              className="filter-select"
              value={categoryFilter}
              onChange={(e) =>
                setState({ categoryFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="marketplace-filter">Marketplace</label>
            <select
              id="marketplace-filter"
              className="filter-select"
              value={marketplaceFilter}
              onChange={(e) =>
                setState({ marketplaceFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Marketplaces</option>
              {marketplaces.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="seller-filter">Seller</label>
            <select
              id="seller-filter"
              className="filter-select"
              value={sellerFilter}
              onChange={(e) =>
                setState({ sellerFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Sellers</option>
              {sellers.map((seller) => (
                <option key={seller} value={seller}>
                  {seller}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label htmlFor="items-per-page">Items per page</label>
            <select
              id="items-per-page"
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) =>
                setState({
                  itemsPerPage: parseInt(e.target.value, 10),
                  currentPage: 1,
                })
              }
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>

          <div className="filter-item">
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={() => fetchProducts(true)}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn-secondary clear-filters-btn"
              onClick={handleClearFilters}
              disabled={loading}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {(searchQuery ||
          categoryFilter ||
          marketplaceFilter ||
          sellerFilter) && (
          <div className="active-filters">
            <small className="text-muted">
              Showing {filteredProducts.length} of {products.length} products
            </small>
            {searchQuery && (
              <span className="filter-badge">Search: "{searchQuery}"</span>
            )}
            {categoryFilter && (
              <span className="filter-badge">Category: {categoryFilter}</span>
            )}
            {marketplaceFilter && (
              <span className="filter-badge">
                Marketplace: {marketplaceFilter}
              </span>
            )}
            {sellerFilter && (
              <span className="filter-badge">Seller: {sellerFilter}</span>
            )}
          </div>
        )}
      </div>

    {selectedProductIds.length > 0 && (
    <div className="selection-bar">
        <span className="selection-count">
            <strong>{selectedProductIds.length}</strong> product(s) selected
        </span>
        <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => setState({ selectedProductIds: [] })}
        >
            Clear Selection
        </button>

        {/* ✅ Bulk Upload Button */}
        <button
            type="button"
            className="btn btn-info btn-sm ms-2"
            onClick={handleBulkUploadVinted}
            disabled={bulkUploading}
        >
            {bulkUploading
                ? 'Starting Bulk Upload...'
                : `Bulk Upload to Vinted (${selectedProductIds.length})`}
        </button>
    </div>
)}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => fetchProducts(true)}
          >
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-5 text-center">
          <h4>Loading products...</h4>
        </div>
      ) : (
        <>
          {paginatedProducts.length > 0 && (
            <div className="select-all-row">
              <input
                type="checkbox"
                id="selectAll"
                checked={
                  paginatedProducts.length > 0 &&
                  paginatedProducts.every((p) =>
                    selectedProductIds.includes(p.id)
                  )
                }
                onChange={handleSelectAll}
              />
              <label htmlFor="selectAll">Select All on Page</label>
            </div>
          )}

          <div className="product-list">
            {paginatedProducts.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-box-open"></i>
                <p>
                  {error
                    ? "Unable to load products."
                    : "No products found matching your filters."}
                </p>
              </div>
            ) : (
              paginatedProducts.map((product) => {
                const orderInfo = orderStatusMap[product.id];
                const isSold = orderInfo && orderInfo.seller_status !== "not_available" && orderInfo.qc_status !== "rejected";
                return (
                <div
                  key={product.id}
                  className={`product-card ${
                    selectedProductIds.includes(product.id) ? "selected" : ""
                  } ${isSold ? "sold" : ""}`}
                >
                  <div className="product-main">
                    <div className="product-main-content">
                      <div className="product-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedProductIds.includes(product.id)}
                          onChange={() => handleSelectProduct(product.id)}
                        />
                      </div>

                   <div style={{ position: "relative", display: "inline-block" }}>
  <RetryImage
    src={product.images[0]}
    alt={product.title}
    className="product-image"
  />
  {/* SOLD badge for products in order workflow (not rejected) */}
  {orderStatusMap[product.id] && (() => {
    const st = orderStatusMap[product.id];
    const isRejected = st.seller_status === "not_available" || st.qc_status === "rejected";
    if (isRejected) return null;
    return (
      <div className="sold-badge">
        ✓ SOLD
      </div>
    );
  })()}
</div>

                       <div className="product-info">
                         <h5 className="product-title">{product.title}</h5>
                         <p className="product-description">
                           {product.description}
                         </p>

                       <div className="product-badges">
                          <span className="product-badge">
                            Price: {formatPriceInner(product.price || 0)}
                          </span>
                          <span className="product-badge">
                            Category: {product.category || "Uncategorized"}
                          </span>
                          <span className="product-badge">
                            Condition:{" "}
                            {product.condition ||
                              product.marketplaceDetails?.ebay?.Condition ||
                              "Not specified"}
                          </span>
                          <span className="product-badge">
                            Quantity: {product.quantity || 0}
                          </span>
                          {product.uploadedBy?.name && (
                            <span className="product-badge">
                              Seller: {product.uploadedBy.name}
                            </span>
                          )}
                        </div>

                        <div className="marketplace-badges">
                          {product.selectedMarketplaces.map((id) => {
                            const market = marketplaces.find(
                              (m) => m.id === id
                            );
                            if (id === "local") return null;
                            return (
                              <span key={id} className="marketplace-badge">
                                {market?.name || id}
                              </span>
                            );
                          })}

                          {/* Status badge */}
                          {product.status && (
                            <span
                              className={`status-badge status-${product.status
                                .toLowerCase()
                                .replace(/\s+/g, "_")}`}
                            >
                              {product.status
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="product-actions">
                        <button
                          type="button"
                          className="btn btn-info btn-sm product-action-btn"
                          onClick={() => handleToggleExpand(product.id)}
                        >
                          {expandedProduct === product.id
                            ? "Collapse"
                            : "Expand"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-danger btn-sm product-action-btn"
                          onClick={() =>
                            navigate(`/products/${product.id}`, {
                              state: { product },
                            })
                          }
                        >
                          View Details
                        </button>

                      {isEditor && (
  <button
    type="button"
    className="btn btn-warning btn-sm product-action-btn"
    onClick={() =>
      navigate(`/products/update/${product.id}`)
    }
  >
    Update
  </button>
)}

                    {   isEditor && (
  <button
    type="button"
    className="btn btn-success btn-sm product-action-btn"
    onClick={async () => {
                            try {
                              const sellerUserId = product.uploadedBy?.id;
                              const existingSku = product.sku;

                              // SKU check
                              let finalSku = existingSku;
                              try {
                                const res = await fetch(
                                  `${BASE_URL}/api/v1/checkAndGetSku?user_id=${encodeURIComponent(
                                    sellerUserId
                                  )}&existing_sku=${encodeURIComponent(
                                    existingSku
                                  )}`
                                );
                                const data = await res.json();
                                if (data.success) {
                                  finalSku = data.sku;
                                }
                              } catch (err) {
                                console.error("SKU check failed:", err);
                              }

                              // Full product data
                              let fullProductData = null;
                              try {
                                const token = localStorage.getItem("token");
                                const fullRes = await fetch(
                                  `${BASE_URL}/api/product-full/${product.id}`,
                                  {
                                    headers: {
                                      Authorization: `Bearer ${token}`,
                                    },
                                  }
                                );
                                const fullJson = await fullRes.json();
                                if (fullJson.success && fullJson.data) {
                                  fullProductData = fullJson.data;
                                }
                              } catch (err) {
                                console.error("Full product fetch failed:", err);
                              }

                              const { product: fullProduct, ebay } =
                                fullProductData || {};

                              let savedFormData = {};
                              if (fullProduct?.raw_response) {
                                try {
                                  let raw = fullProduct.raw_response;
                                  if (typeof raw === "string")
                                    raw = JSON.parse(raw);
                                  if (raw.formData) savedFormData = raw.formData;
                                } catch (err) {
                                  console.warn(
                                    "Failed to parse raw_response",
                                    err
                                  );
                                }
                              }

                              const ebayFields = {};
                              if (ebay && ebay.aspects) {
                                let aspects = ebay.aspects;
                                if (typeof aspects === "string")
                                  aspects = JSON.parse(aspects);
                                Object.entries(aspects).forEach(
                                  ([key, value]) => {
                                    let val = Array.isArray(value)
                                      ? value.join(", ")
                                      : value;
                                    if (
                                      val &&
                                      val !== "N/A" &&
                                      val.trim() !== ""
                                    ) {
                                      let mappedKey = key;
                                      if (key === "Colour") mappedKey = "Color";
                                      ebayFields[mappedKey] = val;
                                    }
                                  }
                                );
                              }

                              const finalBrand =
                                ebayFields.Brand ||
                                savedFormData.brand ||
                                fullProduct?.brand ||
                                "";
                              const finalColor =
                                ebayFields.Color ||
                                savedFormData.color ||
                                fullProduct?.color ||
                                "";
                              const finalMaterial =
                                ebayFields.Material ||
                                savedFormData.material ||
                                fullProduct?.material ||
                                "";
                              const finalTags =
                                savedFormData.tags || fullProduct?.tags || "";
                              const finalVariants = savedFormData.variants
                                ?.length
                                ? savedFormData.variants
                                : [
                                    {
                                      id: 1,
                                      size: ebayFields.Size || "",
                                      color: finalColor,
                                      sku: "",
                                    },
                                  ];
                              const finalCategoryAspects =
                                savedFormData.categoryAspects || [];
                              const finalAllCategoryAspects =
                                savedFormData.allCategoryAspects || [];
                              const finalCategoryId =
                                savedFormData.categoryId || "";
                              const finalConditionScore =
                                savedFormData.condition_score || "";
                              const finalConditionSummary =
                                savedFormData.condition_summary || "";
                              const finalAuthenticityScore =
                                savedFormData.authenticity_score || "";
                              const finalAuthenticitySummary =
                                savedFormData.authenticity_summary || "";
                              const finalVintedSize =
                                savedFormData.vintedSize || ebayFields.Size || "";

                              const formData = {
                                currentStep: 2,
                                formData: {
                                  uploadedImages: fullProduct?.images?.length
                                    ? fullProduct.images
                                    : product.images?.filter(
                                        (img) => !img.includes("placeholder")
                                      ) || [],
                                  blurStatuses: [],
                                  imageProcessing: false,
                                  backgroundRemoval: false,
                                  blurDetection: false,
                                  upscaleImage: false,
                                  title:
                                    fullProduct?.title || product.title || "",
                                  description:
                                    fullProduct?.description ||
                                    product.description ||
                                    "",
                                  sku: finalSku,
                                  _existing_product_id: product.id,
                                  price: String(
                                    fullProduct?.price ?? product.price ?? ""
                                  ),
                                  quantity: String(
                                    fullProduct?.quantity ??
                                      product.quantity ??
                                      "1"
                                  ),
                                  category:
                                    savedFormData.category ||
                                    product.category ||
                                    "",
                                  condition:
                                    fullProduct?.condition ||
                                    product.condition ||
                                    "",
                                  brand: finalBrand,
                                  color: finalColor,
                                  material: finalMaterial,
                                  tags: finalTags,
                                  metafields: savedFormData.metafields || {},
                                  categoryAspects: finalCategoryAspects,
                                  allCategoryAspects: finalAllCategoryAspects,
                                  categoryId: finalCategoryId,
                                  condition_score: finalConditionScore,
                                  condition_summary: finalConditionSummary,
                                  authenticity_score: finalAuthenticityScore,
                                  authenticity_summary:
                                    finalAuthenticitySummary,
                                  variants: finalVariants,
                                  vintedSize: finalVintedSize,
                                  selectedMarketplaces:
                                    product.selectedMarketplaces?.filter(
                                      (m) => m !== "local"
                                    ) || [],
                                  poshmarkCategory:
                                    savedFormData.poshmarkCategory || "Women",
                                  poshmarkSubCategory:
                                    savedFormData.poshmarkSubCategory || "",
                                  vintedCategory:
                                    savedFormData.vintedCategory || "",
                                  vintedCategoryId:
                                    savedFormData.vintedCategoryId || "",
                                  vintedCategoryPath:
                                    savedFormData.vintedCategoryPath || [],
                                  vintedPackageSize:
                                    savedFormData.vintedPackageSize || "medium",
                                  seller_id: sellerUserId,
                                },
                              };

                              const copyId = `copy_${Date.now()}_${Math.random()
                                .toString(36)
                                .substring(2, 8)}`;
                              sessionStorage.setItem(
                                copyId,
                                JSON.stringify(formData)
                              );
                              window.open(
                                `/products/add?copyId=${copyId}`,
                                "_blank"
                              );
                            } catch (error) {
                              console.error(
                                "Error preparing product copy:",
                                error
                              );
                              alert("Kuch galat ho gaya, dubara try karo.");
                            }
                          }}
                        >
                          Add Product
                        </button>
                    )}
                      </div>
                    </div>
                  </div>

                  {expandedProduct === product.id && (
                    <div className="expanded-details">
                      {product.selectedMarketplaces.map((marketplaceId) => {
                        const market = marketplaces.find(
                          (m) => m.id === marketplaceId
                        );
                        const details =
                          product.marketplaceDetails[marketplaceId];
                        if (!details || Object.keys(details).length === 0)
                          return null;

                        return (
                          <div
                            key={marketplaceId}
                            className="marketplace-details-section"
                          >
                            <h6
                              className={`marketplace-title text-${market?.color || "secondary"}`}
                            >
                              {market?.name || marketplaceId.toUpperCase()}{" "}
                              Details
                            </h6>
                            <div className="details-grid">
                              {Object.entries(details)
                                .filter(
                                  ([, value]) =>
                                    value &&
                                    value !== "" &&
                                    value !== "Not specified" &&
                                    value !== "Not listed" &&
                                    value !== "Not synced"
                                )
                                .map(([key, value]) => (
                                  <div key={key} className="detail-item">
                                    <span className="detail-label">{key}</span>
                                    <span className="detail-value">
                                      {typeof value === "object"
                                        ? JSON.stringify(value)
                                        : String(value)}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }))}
          </div>

          {totalPages > 1 && (
            <>
              <div className="pagination">
                <div
                  className={`page-item ${safePage === 1 ? "disabled" : ""}`}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(safePage - 1)}
                    disabled={safePage === 1}
                  >
                    Previous
                  </button>
                </div>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (safePage <= 3) {
                    pageNum = i + 1;
                  } else if (safePage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = safePage - 2 + i;
                  }

                  return (
                    <div
                      key={pageNum}
                      className={`page-item ${
                        safePage === pageNum ? "active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </div>
                  );
                })}

                <div
                  className={`page-item ${
                    safePage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(safePage + 1)}
                    disabled={safePage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
              <div className="pagination-info">
                Page {safePage} of {totalPages} ({filteredProducts.length}{" "}
                items)
              </div>
            </>
          )}
        </>
      )}
    </MDBContainer>
  );
};

export default ViewProductsPage;